import React, { useState, useRef, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "@emotion/react";
import ScoreGraph from "../../../Charts/ScoreGraph";
import QuantGraphDetailed from "../../../Charts/QuantGraphDetailed";
import QuantGraphGlobal from "../../../Charts/QuantGraphGlobal";
import StepperButtons from "../../StepperButtons";
import InformationDialogue from "../../../CustomComponents/InformationDialogue";
import { compare } from "../../../../context/designPhase";
import DownloadIcon from "@mui/icons-material/Download";
import { ensureDesignScores } from "../DesignActions";
import { getDesignBackgroundColors } from "../../../../utils/chartPatterns";

export default function DijkvakNiveauPerMeterTab({ payload }) {
  const styling = useTheme();
  const dispatch = useDispatch();

  // Get Redux state
  const flatKpis = useSelector((state) => state.themes.flatKpis);
  
  // Get data from payload
  const loopKpis = (payload?.loop?.loop_kpis || []).map(lk => ({...lk, kpi_details: flatKpis[lk.kpi]}));
  let loopLayers = payload?.loop?.loop_layers || [];
  loopLayers = [...loopLayers, {id: 'total', layer: 'total', loop: payload.loop.id, layer_details: {description: 'Totaal', name: 'Totaal', project: null}}];
  const loopSegments = payload?.loop?.loop_segments || [];
  const loopThemes = payload?.loop?.loop_themes || [];

  // Get designs (excluding reference design - it will be shown as a line only)
  let designs = [...payload?.loop?.designs || []].sort((a, b) => a.name.localeCompare(b.name));

  // Filter KPIs that have actual segment data
  const segmentKpis = useMemo(() => {
   
    const filtered = loopKpis.filter((lk) => {
      // Must be selected and quantitative
      if (!lk.is_selected) {
        return false;
      }
      
      if (lk.kpi_details.scale_type <= 0) {
        return false;
      }
      
      // Check if any design has segment data for this KPI
      const hasSegmentData = designs.some((design) => {
        const score = design.scores?.find((score) => score.kpi === lk.kpi_details.id);
        if (score?.value && Array.isArray(score.value)) {
          // Check if there's any segment data (not just total)
          const hasSegments = score.value.some((segmentData) => 
            segmentData.segment !== "total" && 
            segmentData.values && 
            segmentData.values.length > 0
          );
          return hasSegments;
        }
        return false;
      });
      
      return hasSegmentData;
    });
    
    return filtered;
  }, [loopKpis, designs]);

  const [selectedKpi, setSelectedKpi] = useState(segmentKpis.length > 0 ? segmentKpis[0].id : null);
  const [selectedDesign, setSelectedDesign] = useState(designs.length > 0 ? designs[0].id : null);

  // Refs for charts
  const chartRef = useRef(null);
  const chartRefs = useRef({});

  // Effect to ensure all designs have the required scores
  useEffect(() => {
    const ensureScoresForAllDesigns = async () => {
      for (const design of designs) {
        const selectedKpiIds = segmentKpis.map(kpi => kpi.kpi_details.id);
        const missingScores = selectedKpiIds.filter(kpiId => {
          return !design.scores?.some(score => score.kpi === kpiId);
        });

        if (missingScores.length > 0) {
          await ensureDesignScores(design.id, payload?.phase?.id, payload?.loop?.id, dispatch);
        }
      }
    };

    if (designs.length > 0 && segmentKpis.length > 0) {
      ensureScoresForAllDesigns();
    }
  }, [designs, segmentKpis, dispatch, payload?.phase?.id, payload?.loop?.id]);

  const handleKpiChange = (kpiId) => {
    setSelectedKpi(kpiId);
  };

  const handleDesignChange = (event) => {
    setSelectedDesign(event.target.value);
  };

  const handleDownloadChart = (chartKey) => {
    const chartRefElement = chartRefs.current[chartKey];
    if (chartRefElement?.canvas) {
      const canvas = chartRefElement.canvas;
      const link = document.createElement("a");
      const phaseName = payload.phase?.phase_name || `Fase_${payload.phase?.id || 'unknown'}`;
      
      // Calculate loop number by finding the loop index within the phase
      let loopNumber = 'unknown';
      if (payload.phase?.loops && payload.loop?.id) {
        const loopIndex = payload.phase.loops.findIndex(loop => loop.id === payload.loop.id);
        if (loopIndex !== -1) {
          loopNumber = loopIndex + 1;
        }
      }
      
      link.download = `${chartKey}_dijkvak_per_meter_${phaseName}_Ontwerpronde_${loopNumber}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    }
  };

  // Data constructor for per-meter segment-based graphs (dijkvakken on x-axis, layers as data series, values divided by segment length)
  const segmentPerMeterDataConstructor = (kpiId) => {
    const currentDesign = designs.find(design => design.id === selectedDesign);
        
    if (!currentDesign) {
      console.error('No design selected');
      return [];
    }
    
    // Create data for each layer (onderdeel) for the selected design
    const dataSeries = loopLayers.map((loopLayer, layerIndex) => {
      const layerColor = Object.values(styling.palette.graphics)[layerIndex % Object.values(styling.palette.graphics).length];
      
      const layerValues = loopSegments.map((segment) => {
        // Get values for this layer from the selected design
        const score = currentDesign.scores?.find((score) => score.kpi === kpiId);
        
        if (score?.value) {
          const segmentData = score.value.find(s => s.segment === segment.id);
          if (segmentData) {
            const layerValue = segmentData.values.find(lv => lv.layer === loopLayer.id);
            if (layerValue) {
              const totalValue = Number(layerValue.value);
              const segmentLength = segment.length || 1; // Use 1 as fallback to avoid division by zero
              const perMeterValue = totalValue / segmentLength;
              return perMeterValue;
            }
          }
        }
        return 0;
      });
      
      
      return {
        label: loopLayer.layer_details.name,
        data: layerValues,
        type: "bar",
        backgroundColor: layerColor,
        legendColor: layerColor,
        order: 2, // Lower order to appear behind lines
      };
    });
    
    // Add reference design as a line if it exists (it's never in the designs array now)
    const referenceDesign = payload?.loop?.reference_design;
    
    if (referenceDesign) {
      const referenceValues = loopSegments.map((segment) => {
        // Find the score for this KPI in the reference design
        const score = referenceDesign.scores?.find((score) => score.kpi === kpiId);
        
        if (score?.value) {
          // Find the segment data
          const segmentData = score.value.find(s => s.segment === segment.id);
          if (segmentData) {
            const totalValue = segmentData.values.reduce((acc, layerScore) => acc + Number(layerScore.value), 0);
            const segmentLength = segment.length || 1; // Use 1 as fallback to avoid division by zero
            const perMeterValue = totalValue / segmentLength;
            return perMeterValue;
          }
        }
        return 0;
      });
      
      // Add reference line as a separate data series
      const referenceLineData = {
        label: `Referentie (${referenceDesign.name})`,
        data: referenceValues,
        type: "line",
        borderColor: "#FF6B35", // Orange color for reference
        backgroundColor: "#FF6B35",
        borderWidth: 3,
        pointStyle: "circle",
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: false,
        tension: 0,
        legendColor: "#FF6B35",
        order: 0, // Lower order to appear on top of bars
      };
      
      dataSeries.push(referenceLineData);
    } else {
      console.error('No reference design found');
    }
    
    return dataSeries;
  };

  if (designs.length === 0) {
    return (
      <Box>
        <Typography>
          Er zijn nog geen ontwerpen voor deze ontwerpronde. Voeg een ontwerp toe om de scores te kunnen bekijken.
        </Typography>
        <StepperButtons payload={payload} />
      </Box>
    );
  }

  if (segmentKpis.length === 0) {
    return (
      <Box>
        <Typography>
          Er zijn geen KPI's met dijkvak data beschikbaar voor deze ontwerpronde. 
          Alleen KPI's die in de toetsen sectie tabellen hebben (met dijkvak-specifieke data) worden hier getoond.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Ga naar de "Toetsen" stap om dijkvak data in te vullen voor de KPI's.
        </Typography>
        <StepperButtons payload={payload} />
      </Box>
    );
  }

  const currentKpi = segmentKpis.find((kpi) => kpi.id === selectedKpi);

  // Find relevant loop themes and goals for the selected KPI
  const relevantLoopThemes = loopThemes.filter((loopTheme) =>
    loopTheme.goals.some((goal) => goal.kpi === currentKpi?.kpi_details?.id)
  );

  return (
    <Box>
      <InformationDialogue text={compare} />

      {/* KPI Selection buttons */}
      <Box sx={{ mb: 3, mt: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h6">Selecteer KPI:</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {segmentKpis.map((kpi) => (
            <Tooltip key={kpi.id} title={kpi.kpi_details.description || ""} enterDelay={1000}>
              <Button
                onClick={() => handleKpiChange(kpi.id)}
                variant={selectedKpi === kpi.id ? "contained" : "outlined"}
                size="small"
                sx={{
                  minWidth: 120,
                  textTransform: "none",
                  fontWeight: selectedKpi === kpi.id ? "bold" : "normal",
                  borderRadius: 3,
                }}
              >
                <Typography variant="body2">{kpi.kpi_details.name}{kpi.kpi_details.unit && ` (${kpi.kpi_details.unit})`}</Typography>
              </Button>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {/* Design selector */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box>
            <Typography variant="h6" gutterBottom>
              Selecteer Ontwerp
            </Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="design-selector-label">Ontwerp</InputLabel>
              <Select
                labelId="design-selector-label"
                value={selectedDesign || ''}
                label="Ontwerp"
                onChange={handleDesignChange}
              >
                {designs.map((design) => (
                  <MenuItem key={design.id} value={design.id}>
                    {design.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Box>

      {/* Display the selected KPI per-meter graphs */}
      <Box>
        {currentKpi?.kpi_details?.scale_type > 0 && (
          // Quantitative KPIs with segment data
          <Grid container spacing={1}>
            {/* Display per-meter graphs for each relevant goal */}
            {relevantLoopThemes.map((loopTheme) =>
              loopTheme.goals
                .filter((goal) => goal.kpi === currentKpi.kpi_details.id && flatKpis[goal.kpi]?.scale_type > 0)
                .map((loopGoal, goalIndex) => {
                  const kpi = flatKpis[loopGoal.kpi];
                  const perMeterChartKey = `${kpi.name}-dijkvak-per-meter`;

                  return (
                    <Grid
                      sx={{ maxHeight: 400, minHeight: 200, minWidth: 400 }}
                      item
                      xs={12}
                      xl={6}
                      key={`goal_${loopGoal.id}`}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {kpi.name} per meter - Dijkvak niveau ({designs.find(d => d.id === selectedDesign)?.name || 'Geen ontwerp geselecteerd'})
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadChart(perMeterChartKey)}
                          sx={{ textTransform: "none", minWidth: 80 }}
                        >
                          Download
                        </Button>
                      </Box>
                      <QuantGraphDetailed
                        ref={(el) => (chartRefs.current[perMeterChartKey] = el)}
                        variants={loopSegments.map((segment) => segment.name)}
                        initData={segmentPerMeterDataConstructor(currentKpi.kpi_details.id)}
                        kpi={kpi}
                        goal={loopGoal}
                        showGoalLine={false}
                        layers={loopLayers}
                        segments={loopSegments}
                      />
                    </Grid>
                  );
                })
            )}
          </Grid>
        )}
      </Box>
    </Box>
  );
}

DijkvakNiveauPerMeterTab.propTypes = {
  payload: PropTypes.object.isRequired,
};

